import type { Theme } from "../types/Theme";
import { alpha, alphaByPercentage } from "./Mixins";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";

/** Opacity applied to the active statement border for inactive modern statement cards. */
export const CARROUSEL_INACTIVE_CORNER_BORDER_ALPHA = 0.45;

function resolveCarrouselCornerBorderBaseColor(theme: Theme): string | null {
    const colorKey = theme.CarrouselCornerBorderColor;
    if (!colorKey || colorKey === "NoColor") {
        return null;
    }

    const keyedColor = (theme as Record<string, string | null | undefined>)[colorKey];
    const baseColor =
        keyedColor ?? (colorKey.startsWith("#") ? colorKey : theme.ControlBorderColor);

    if (!baseColor) {
        return null;
    }

    return baseColor;
}

/**
 * Resolves the carousel statement card border color from {@link Theme.CarrouselCornerBorderColor}
 * (a theme color key or literal) and {@link Theme.CarrouselCornerBorderColorPercent}.
 * Returns null when the setting is unset or maps to {@link Theme.NoColor}.
 */
export function resolveCarrouselCornerBorderColor(
    theme: Theme,
    options?: { inactive?: boolean },
): string | null {
    const baseColor = resolveCarrouselCornerBorderBaseColor(theme);
    if (!baseColor) {
        return null;
    }

    const activeColor = alphaByPercentage(baseColor, theme.CarrouselCornerBorderColorPercent ?? "100");
    if (options?.inactive && shouldUseModernTheme(theme)) {
        return alpha(activeColor, CARROUSEL_INACTIVE_CORNER_BORDER_ALPHA);
    }

    return activeColor;
}

export function resolveCarrouselCornerBorderWidth(theme: Theme): number {
    return theme.ChoiceListBackgroundBorderWidth ?? theme.BorderWidth ?? 1;
}

export function resolveCarrouselCornerBorder(
    theme: Theme,
    options?: { inactive?: boolean },
): string {
    const color = resolveCarrouselCornerBorderColor(theme, options);
    if (!color) {
        return "none";
    }
    return `${resolveCarrouselCornerBorderWidth(theme)}px solid ${color}`;
}
